import { z } from 'zod'
import { furnitureItemDtoSchema } from './furniture-item.dto.js'

/** One member of a furniture group: a catalog item at a relative transform. */
export const furnitureGroupMemberDtoSchema = z.object({
  furnitureItemId: z.string().uuid(),
  item: furnitureItemDtoSchema,
  x: z.number(),
  y: z.number(),
  rotation: z.number(),
})

export type FurnitureGroupMemberDto = z.infer<typeof furnitureGroupMemberDtoSchema>

/**
 * A reusable arrangement of catalog furniture — e.g. a round table with eight
 * chairs around it. Placing it on a plan drops its members as individual pieces
 * that each draw down their item's inventory. It belongs to one venue, or to a
 * vendor, and holds only that owner's pieces.
 */
export const furnitureGroupDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The venue that owns this arrangement; null for a vendor's. */
  venueId: z.string().uuid().nullable(),
  members: z.array(furnitureGroupMemberDtoSchema),
  /**
   * The key an importer knows this arrangement by, or null for one somebody built
   * by hand. Exposed so a second import of the same setting finds the entry it
   * made last time instead of adding another — the arrangement is the same
   * whatever a person has since renamed it to.
   */
  importKey: z.string().nullable(),
})

export type FurnitureGroupDto = z.infer<typeof furnitureGroupDtoSchema>

export const furnitureGroupListDtoSchema = z.object({
  groups: z.array(furnitureGroupDtoSchema),
  total: z.number().int().nonnegative(),
})

export type FurnitureGroupListDto = z.infer<typeof furnitureGroupListDtoSchema>
